import axios from "axios";
import qs from "qs";
export default class MsxServiceClient {
    constructor(config) {
        this.msxConfig = config;
    }
    async getAllOptionsValuesFromMsx(tenant, locale, version, productKey) {
        const experienceAndOrdering = await this.getExperienceAndOrdering(tenant, locale, version, productKey);
        if (experienceAndOrdering) {
            const optionValues = this.getOptionValuesFromExperienceAndOrdering(experienceAndOrdering);
            return optionValues;
        }
        return {};
    }
    async getOptionValuesFromMsx(tenant, locale, version, productKey) {
        const experienceAndOrdering = await this.getExperienceAndOrdering(tenant, locale, version, productKey);
        if (experienceAndOrdering) {
            const optionValues = this.getOptionValuesFromExperienceAndOrdering(experienceAndOrdering, [
                "Product Page",
            ]);
            return optionValues;
        }
        return {};
    }
    async getExperienceAndOrdering(tenant, locale, version, productKey) {
        let getMsxUrl = `${this.msxConfig.baseUrl}/tenant/${tenant}/productKey/${productKey}/version/${version}/experienceandordering`;
        getMsxUrl = getMsxUrl + `?requestor=${this.msxConfig.requestor}`;
        getMsxUrl = getMsxUrl + `&cultures=${locale}`;
        try {
            const result = (await axios.get(getMsxUrl)).data;
            const experienceAndOrdering = result === null || result === void 0 ? void 0 : result[locale];
            return experienceAndOrdering;
        }
        catch (e) {
            console.error(e);
            throw e;
        }
    }
    getOptionValuesFromExperienceAndOrdering(experienceAndOrdering, experienceKeys = []) {
        const filteredExperienceValues = experienceAndOrdering.optionsByExperience
            .filter(experience => !experienceKeys.length || experienceKeys.includes(experience.key))
            .map(experience => experience === null || experience === void 0 ? void 0 : experience.value)
            .filter(e => !!e);
        const idMap = {};
        const optionValues = {};
        filteredExperienceValues.forEach(experienceValue => {
            experienceValue.forEach((id) => {
                idMap[id] = experienceAndOrdering.references[id].key;
                const isKey = !!experienceAndOrdering.attributeAndValueOrdering.find((entry) => entry.key === id);
                if (isKey && !optionValues[idMap[id]]) {
                    optionValues[idMap[id]] = [];
                }
                else {
                    const { key } = experienceAndOrdering.attributeAndValueOrdering.find((entry) => entry.value.includes(id)) || {};
                    if (key && idMap[key]) {
                        if (optionValues[idMap[key]]) {
                            optionValues[idMap[key]].push(idMap[id]);
                        }
                        else {
                            optionValues[idMap[key]] = [idMap[id]];
                        }
                    }
                }
            });
        });
        return optionValues;
    }
    async getProductKey(mpvId, locale, tenant) {
        var _a;
        const queryParams = {
            cultures: [locale],
            lower: true,
            requestor: this.msxConfig.requestor,
        };
        const queryString = qs.stringify(queryParams, { arrayFormat: "repeat" });
        const url = `${this.msxConfig.baseUrl}/tenant/${tenant}/mpvs/${mpvId}?${queryString}`;
        try {
            const result = await axios.get(url);
            return (_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a[locale.toLowerCase()];
        }
        catch (ex) {
            console.error(ex);
        }
        return undefined;
    }
    async getMerchandisedProperties(tenant, locale, productKey) {
        const url = `${this.msxConfig.baseUrl}/tenant/${tenant}/locale/${locale}/productKey/${productKey}/merchandisedProperties?requestor=${this.msxConfig.requestor}`;
        try {
            const result = await axios.get(url);
            return result.data;
        }
        catch (ex) {
            console.error(ex);
        }
        return undefined;
    }
    async getQuantities(locale, tenant, productKey, version) {
        var _a;
        const queryParams = {
            cultures: [locale],
            lower: true,
            requestor: this.msxConfig.requestor,
        };
        const queryString = qs.stringify(queryParams, { arrayFormat: "repeat" });
        const url = `${this.msxConfig.baseUrl}/tenant/${tenant}/productKey/${productKey}/version/${version}/quantities?${queryString}`;
        try {
            const result = await axios.get(url);
            return (_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a[locale.toLowerCase()];
        }
        catch (ex) {
            console.error(ex);
        }
        return undefined;
    }
    async getMsxData(locale, productKey, version, tenant) {
        const merchandisedPropertyPromise = this.getMerchandisedProperties(tenant, locale, productKey);
        const quantityPromise = this.getQuantities(locale, tenant, productKey, version);
        const experienceAndOrderingPromise = this.getExperienceAndOrdering(tenant, locale, version, productKey);
        const [merchandisedProperty, quantity, experienceAndOrdering] = await Promise.all([
            merchandisedPropertyPromise,
            quantityPromise,
            experienceAndOrderingPromise,
        ]);
        return {
            merchandisedProperty,
            quantity,
            experienceAndOrdering,
        };
    }
    parseExperienceAndOrderingData(experience, data) {
        var _a;
        if (data) {
            const calloutDict = data.callouts.reduce((result, callout) => {
                result[callout.key] = callout.value;
                return result;
            }, {});
            // This dictionary maps from a reference ID to an object of MsxExperienceAndOrderingOptionAndValues type
            // The reference IDs used as key are the option reference IDs.
            const allOptionValues = data.attributeAndValueOrdering.reduce((result, attributeAndValue) => {
                const optionReference = attributeAndValue.key;
                const valueReferences = attributeAndValue.value;
                const option = data.references[optionReference];
                const optionValues = [];
                const optionRanges = [];
                valueReferences.forEach(valueReference => {
                    var _a;
                    const reference = data.references[valueReference];
                    if (reference.range === true) {
                        const rangeReference = reference;
                        if (rangeReference) {
                            optionRanges.push(rangeReference);
                        }
                    }
                    else {
                        const optionValueReference = reference;
                        const isPreselected = ((_a = data.preselects[valueReference]) === null || _a === void 0 ? void 0 : _a[experience]) || false;
                        const callouts = calloutDict[valueReference] || [];
                        if (optionValueReference) {
                            optionValues.push(Object.assign(Object.assign({}, optionValueReference), { isPreselected,
                                callouts }));
                        }
                    }
                });
                if (option && optionValues.length > 0) {
                    const optionAndValues = {
                        key: option,
                        values: optionValues,
                    };
                    result[optionReference] = optionAndValues;
                }
                if (option && optionRanges.length > 0) {
                    const optionAndRanges = {
                        key: option,
                        ranges: optionRanges,
                    };
                    result[optionReference] = optionAndRanges;
                }
                return result;
            }, {});
            const optionsByExperience = (_a = data.optionsByExperience.find(exp => exp.key === experience)) === null || _a === void 0 ? void 0 : _a.value;
            const options = (optionsByExperience === null || optionsByExperience === void 0 ? void 0 : optionsByExperience.map(referenceId => allOptionValues[referenceId]).filter(o => !!o)) || [];
            return options;
        }
        return [];
    }
}
//# sourceMappingURL=MsxService.js.map